// ─── reviewExport ───────────────────────────────────────────────────────────
// XLSX + JSON exports for the Review Pipeline page.
//
// Workbook layout (post-2025-slide refresh):
//   Sheet 1 — Funnel        Total Opportunities + Dead / Tracking / Active
//                           breakdown (per lane: total + off/on splits +
//                           LOIs + B&F). Mirrors the on-screen visual.
//   Sheet 2 — Deals         The filtered deals table: address, MSA, stage,
//                           SF, ask, cap, broker, made-B&F, killed at,
//                           killed reason, killed narrative.
//   Sheet 3 — Competition   Per-deal bid detail: our bid PSF/total, winning
//                           bid PSF/total, delta, winner, num bidders,
//                           broker debrief. Includes the market-clearing
//                           row (actual sale price + buyer + close date)
//                           when we've captured it via the Comps > Passed
//                           Deals Awaiting Sale flow.
//
// Every workbook uses the PP brand pattern: red banner (row 1), dark
// subtitle (row 2), Montserrat throughout, zebra striping on the body.

import * as XLSX from "xlsx-js-style";

const PP = {
  red: "E93529", redDark: "C42D22", dark: "333333", light: "F4F4F4",
  gray: "D8D8D8", white: "FFFFFF", muted: "6B7280",
};

const bannerStyle = {
  font: { name: "Montserrat", bold: true, sz: 16, color: { rgb: PP.white } },
  fill: { patternType: "solid", fgColor: { rgb: PP.red } },
  alignment: { vertical: "center", horizontal: "left", indent: 1 },
};
const subtitleStyle = {
  font: { name: "Montserrat", sz: 10, color: { rgb: PP.white } },
  fill: { patternType: "solid", fgColor: { rgb: PP.dark } },
  alignment: { vertical: "center", horizontal: "left", indent: 1 },
};
const headerStyle = {
  font: { name: "Montserrat", bold: true, sz: 10, color: { rgb: PP.white } },
  fill: { patternType: "solid", fgColor: { rgb: PP.red } },
  alignment: { vertical: "center", horizontal: "left" },
  border: {
    top:    { style: "thin", color: { rgb: PP.redDark } },
    bottom: { style: "thin", color: { rgb: PP.redDark } },
    left:   { style: "thin", color: { rgb: PP.redDark } },
    right:  { style: "thin", color: { rgb: PP.redDark } },
  },
};
function bodyStyle(rowIdx, colIdx, opts = {}) {
  return {
    font: { name: "Montserrat", sz: 10, color: { rgb: PP.dark }, bold: !!opts.bold },
    fill: rowIdx % 2 === 0 ? { patternType: "solid", fgColor: { rgb: PP.light } } : undefined,
    alignment: { vertical: "center", horizontal: opts.right ? "right" : "left", wrapText: !!opts.wrap },
    border: { bottom: { style: "thin", color: { rgb: PP.gray } } },
    ...(opts.numFmt ? { numFmt: opts.numFmt } : {}),
    ...(opts.pct ? { numFmt: '0.0"%"' } : {}),
  };
}

function makeStyledSheet({ title, subtitle, columns, rows }) {
  const header = columns.map(c => c.header);
  const aoa = [
    [title],
    [subtitle],
    [],
    header,
    ...rows,
  ];
  const sh = XLSX.utils.aoa_to_sheet(aoa);

  sh["A1"].s = bannerStyle;
  sh["A2"].s = subtitleStyle;
  sh["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } },
  ];

  columns.forEach((c, i) => {
    const addr = XLSX.utils.encode_cell({ r: 3, c: i });
    if (!sh[addr]) return;
    sh[addr].s = headerStyle;
  });

  rows.forEach((r, rIdx) => {
    columns.forEach((c, cIdx) => {
      const addr = XLSX.utils.encode_cell({ r: 4 + rIdx, c: cIdx });
      if (!sh[addr]) return;
      sh[addr].s = bodyStyle(rIdx, cIdx, { right: !!c.right, pct: !!c.pct, numFmt: c.numFmt, wrap: !!c.wrap, bold: cIdx === 0 && !!c.firstBold });
    });
  });

  sh["!cols"] = columns.map(c => ({ wch: c.width || 16 }));
  sh["!rows"] = [{ hpt: 32 }, { hpt: 18 }, { hpt: 8 }, { hpt: 22 }];
  sh["!sheetViews"] = [{ showGridLines: false }];
  const lastCol = XLSX.utils.encode_col(columns.length - 1);
  sh["!ref"] = `A1:${lastCol}${4 + rows.length}`;
  return sh;
}

const num  = (n) => (n == null || !Number.isFinite(Number(n)) ? "" : Number(n));
const pctV = (n) => (n == null || !Number.isFinite(Number(n)) ? "" : Number(n));
const dateShort = (d) => (d ? String(d).slice(0, 10) : "");

// ── Funnel sheet (slide-shaped) ─────────────────────────────────
function funnelSheet(funnel, subtitleSuffix) {
  const columns = [
    { header: "Lane",       width: 14, firstBold: true },
    { header: "Bucket",     width: 22 },
    { header: "Total",      width: 10, right: true },
    { header: "LOIs",       width: 10, right: true },
    { header: "Best & Final", width: 14, right: true },
  ];
  const rows = [];
  const total = funnel?.total ?? 0;
  rows.push(["Total Opportunities", "", total, "", ""]);

  const laneRows = [
    { key: "dead",     name: "Dead" },
    { key: "tracking", name: "Tracking" },
    { key: "active",   name: "Active" },
  ];
  for (const l of laneRows) {
    const lane = funnel.lanes[l.key];
    if (!lane) continue;
    rows.push([l.name, "", lane.total, "", ""]);
    if (l.key === "tracking") {
      // No off/on split for Tracking
      const stages = Object.entries(lane.byStage || {}).sort();
      for (const [s, c] of stages) rows.push([l.name, `  ${s}`, c, "", ""]);
      continue;
    }
    rows.push([l.name, "  Off-Market", lane.offMarket.total, lane.offMarket.lois, lane.offMarket.bestFinal]);
    rows.push([l.name, "  On-Market",  lane.onMarket.total,  lane.onMarket.lois,  lane.onMarket.bestFinal]);
    const stages = Object.entries(lane.byStage || {}).sort();
    for (const [s, c] of stages) rows.push([l.name, `  · ${s}`, c, "", ""]);
  }

  return makeStyledSheet({
    title:    "ProximityParks  ·  Review Pipeline Funnel",
    subtitle: subtitleSuffix,
    columns,
    rows,
  });
}

// ── Deals sheet ────────────────────────────────────────────────
function dealsSheet(rows, subtitleSuffix) {
  const columns = [
    { header: "Address",         width: 40, firstBold: true },
    { header: "MSA(s)",          width: 22 },
    { header: "Stage",           width: 16 },
    { header: "Buy-Box",         width: 12 },
    { header: "SF",              width: 12, right: true, numFmt: "#,##0" },
    { header: "Asking ($)",      width: 14, right: true, numFmt: '"$"#,##0' },
    { header: "Cap %",           width: 10, right: true, pct: true },
    { header: "Broker",          width: 26 },
    { header: "B&F",             width: 8,  right: true },
    { header: "Killed",          width: 12 },
    { header: "Reason",          width: 24 },
    { header: "Narrative",       width: 50, wrap: true },
    { header: "Created",         width: 12 },
  ];
  const rowFor = (r) => {
    const raw = r._raw || {};
    return [
      r.address,
      (r.msas || []).join(", "),
      r.stage || "",
      r.buyBoxVerdict === "in_box" ? "In-Box" : r.buyBoxVerdict === "filtered" ? "Filtered" : "",
      num(r.sf), num(r.askingPrice), pctV(r.capRate),
      r.broker || "",
      raw.madeBestAndFinal ? "Y" : "",
      dateShort(raw.killedAt),
      r.killedReason || "",
      raw.killedNarrative || "",
      dateShort(r.created_at),
    ];
  };
  return makeStyledSheet({
    title:    "ProximityParks  ·  Deals",
    subtitle: subtitleSuffix,
    columns,
    rows: rows.map(rowFor),
  });
}

// ── Competition + Market Clearing sheet ────────────────────────
function competitionSheet(rows, competitionByDeal, subtitleSuffix) {
  const columns = [
    { header: "Address",           width: 40, firstBold: true },
    { header: "Stage",             width: 14 },
    { header: "Bidders",           width: 10, right: true },
    { header: "Our $/SF",          width: 12, right: true, numFmt: '"$"#,##0.00' },
    { header: "Our Total",         width: 14, right: true, numFmt: '"$"#,##0' },
    { header: "Winning $/SF",      width: 14, right: true, numFmt: '"$"#,##0.00' },
    { header: "Winning Total",     width: 14, right: true, numFmt: '"$"#,##0' },
    { header: "Δ $/SF",            width: 10, right: true, numFmt: '"$"#,##0.00' },
    { header: "Δ %",               width: 8,  right: true, pct: true },
    { header: "Winner",            width: 24 },
    { header: "Lost Reason",       width: 22 },
    { header: "B&F",               width: 8,  right: true },
    { header: "Broker Debrief",    width: 50, wrap: true },
    { header: "Actual Sale",       width: 14, right: true, numFmt: '"$"#,##0' },
    { header: "Actual $/SF",       width: 14, right: true, numFmt: '"$"#,##0.00' },
    { header: "Buyer",             width: 24 },
    { header: "Closed",            width: 12 },
  ];
  const filtered = rows.filter(r => {
    const raw = r._raw || {};
    const c = competitionByDeal.get(r.id);
    return c || raw.marketClearedAt || raw.marketClearedPrice;
  });
  const rowFor = (r) => {
    const raw = r._raw || {};
    const c = competitionByDeal.get(r.id) || {};
    return [
      r.address, r.stage || "",
      num(c.num_bidders_estimated),
      num(c.our_final_bid_psf), num(c.our_final_bid_total),
      num(c.winning_bid_psf),   num(c.winning_bid_total),
      num(c.delta_to_winner_psf), pctV(c.delta_pct),
      c.winner_name || "",
      c.lost_reason || "",
      c.made_best_and_final ? "Y" : "",
      c.broker_debrief_notes || "",
      num(raw.marketClearedPrice), num(raw.marketClearedPricePsf),
      raw.marketClearedBuyer || "",
      dateShort(raw.marketClearedAt),
    ];
  };
  return makeStyledSheet({
    title:    "ProximityParks  ·  Competition + Market Clearing",
    subtitle: subtitleSuffix,
    columns,
    rows: filtered.map(rowFor),
  });
}

function subtitle(prefix, dateLabel, msaFilter) {
  const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const scope = msaFilter && msaFilter.length > 0 ? ` · MSAs: ${msaFilter.join(", ")}` : "";
  return `${prefix}  ·  ${dateLabel}${scope}  ·  Exported ${dateStr}`;
}

export function exportReviewXLSX({ funnel, dealsListings, competition, dateLabel, msaFilter }) {
  const wb = XLSX.utils.book_new();
  wb.Props = { Title: `Review Pipeline — ${dateLabel}`, Author: "ProximityParks", Company: "ProximityParks", CreatedDate: new Date() };

  const compsByDeal = new Map((competition || []).map(c => [c.deal_id, c]));

  XLSX.utils.book_append_sheet(wb, funnelSheet(funnel, subtitle("Funnel", dateLabel, msaFilter)), "Funnel");
  XLSX.utils.book_append_sheet(wb, dealsSheet(dealsListings, subtitle("Deals", dateLabel, msaFilter)), "Deals");
  XLSX.utils.book_append_sheet(wb, competitionSheet(dealsListings, compsByDeal, subtitle("Competition", dateLabel, msaFilter)), "Competition");

  const safeLabel = String(dateLabel || "review").replace(/[^A-Za-z0-9_-]+/g, "_");
  XLSX.writeFile(wb, `PPUSA_ReviewPipeline_${safeLabel}.xlsx`);
}

export function exportReviewJSON({ funnel, dealsListings, competition, dateLabel, msaFilter }) {
  const payload = {
    exportedAt: new Date().toISOString(),
    dateLabel,
    msaFilter,
    funnel,
    dealsListings,
    competition,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeLabel = String(dateLabel || "review").replace(/[^A-Za-z0-9_-]+/g, "_");
  a.download = `PPUSA_ReviewPipeline_${safeLabel}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
